import React, { useMemo, useState } from "react";
import type { ChatSummary } from "./types";
import { Button } from "../../../components/tailwind";
import { ChatDeleteDialog } from "./ChatDeleteDialog";

type ChatSelectMenuProps = {
  summaries: ChatSummary[];
  onChatSelect: (chatId: number) => void;
  onChatCreate: () => void;
  onChatDelete: (chatId: number) => void;
  activeChatId?: number;
};

export const ChatSelectMenu: React.FC<ChatSelectMenuProps> = ({
  summaries,
  onChatSelect,
  onChatCreate,
  onChatDelete,
  activeChatId,
}) => {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const [deleteDialogChatId, setDeleteDialogChatId] = useState<number | null>(
    null
  );
  const [chatSearchQuery, setChatSearchQuery] = useState("");

  const filteredSummaries = useMemo(() => {
    const q = chatSearchQuery.trim().toLowerCase();
    if (!q) {
      return summaries;
    }
    return summaries.filter((s) => s.name.toLowerCase().includes(q));
  }, [summaries, chatSearchQuery]);

  const handleChatDeleteClick = (
    e: React.MouseEvent,
    chatSummary: ChatSummary
  ): void => {
    e.stopPropagation();
    onChatSelect(chatSummary.id);
    setDeleteDialogChatId(chatSummary.id);
  };

  const handleDeleteConfirm = (chatId: number): void => {
    onChatDelete(chatId);
    setDeleteDialogChatId(null);
  };

  const renderChatLink = (chatSummary: ChatSummary): React.JSX.Element => {
    const isDeleteModalOpen = deleteDialogChatId === chatSummary.id;

    return (
      <li key={chatSummary.id} className="tw-my-1 tw-h-10">
        <div
          onClick={(e) => {
            e.stopPropagation();
            onChatSelect(chatSummary.id);
            setDeleteDialogChatId(null);
          }}
          className={`tw-transition-colors tw-group tw-rounded-md tw-flex tw-items-center tw-justify-between tw-relative tw-p-1 hover:tw-bg-gray-100 ${
            activeChatId === chatSummary.id
              ? "tw-bg-gray-100 hover:!tw-shadow-none hover:!tw-brightness-100"
              : ""
          }`}
        >
          {!isDeleteModalOpen ? (
            <>
              <h3
                className={`tw-mx-3 tw-my-0 tw-text-sm tw-text-gray-400 group-hover:tw-text-gray-600 ${
                  activeChatId === chatSummary.id ? "tw-text-gray-600" : ""
                }`}
              >
                {chatSummary.name}
              </h3>

              <Button
                onClick={(e) => handleChatDeleteClick(e, chatSummary)}
                compact="very"
                borderless
                className="!tw-pl-1"
                variant="transparent"
                data-balloon="Delete Chat"
                data-balloon-pos="right"
                icon
              >
                <i className="tw-transition-colors tw-text-gray-500/0 group-hover:tw-text-gray-500/75 material-icons tw-text-xl ">
                  delete
                </i>
              </Button>
            </>
          ) : (
            <ChatDeleteDialog
              id={chatSummary.id}
              open={isDeleteModalOpen}
              setOpen={(open) =>
                setDeleteDialogChatId(open ? chatSummary.id : null)
              }
              confirmAction={() => handleDeleteConfirm(chatSummary.id)}
            />
          )}
        </div>
      </li>
    );
  };

  return (
    <nav>
      <div className="tw-relative tw-flex tw-flex-col tw-items-start tw-gap-2 -tw-mt-1 tw-mb-2 -tw-ml-1">
        <button
          className="tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-text-secondary"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          data-balloon="Toggle Sidebar"
          data-balloon-pos="up-left"
        >
          <i className="material-icons tw-text-2xl">auto_awesome_mosaic</i>
          {sidebarOpen && <span>Chats</span>}
        </button>
        {!sidebarOpen && (
          <button
            className="tw-rounded-lg tw-text-secondary"
            onClick={onChatCreate}
            data-balloon="Start New Conversation"
            data-balloon-pos="down-left"
          >
            <i className="material-icons tw-text-2xl">add</i>
          </button>
        )}
      </div>
      {sidebarOpen && (
        <div className="tw-w-[20rem]">
          <Button
            variant="primary"
            className="tw-w-full tw-my-2"
            onClick={onChatCreate}
          >
            <div className="tw-flex tw-justify-center tw-items-center tw-gap-2">
              <span>New chat</span> <i className="material-icons ">add</i>
            </div>
          </Button>

          <div className="txu-divider"></div>

          <div className="tw-relative tw-w-full tw-my-2">
            <i className="material-icons tw-absolute tw-left-2 tw-top-1/2 -tw-translate-y-1/2 tw-text-gray-400 tw-pointer-events-none tw-text-lg">
              search
            </i>
            <input
              type="search"
              value={chatSearchQuery}
              onChange={(e) => setChatSearchQuery(e.target.value)}
              placeholder="Search chats..."
              className="tw-w-full tw-pl-9 tw-pr-8 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-gray-400 focus:tw-border-transparent"
              aria-label="Search chats"
            />
            {chatSearchQuery ? (
              <button
                type="button"
                onClick={() => setChatSearchQuery("")}
                className="tw-absolute tw-right-2 tw-top-1/2 -tw-translate-y-1/2 tw-text-gray-400 hover:tw-text-gray-600"
                aria-label="Clear search"
              >
                <i className="material-icons tw-text-lg">close</i>
              </button>
            ) : null}
          </div>

          <ul className="tw-my-4 tw-p-0 tw-m-0 tw-list-none">
            {filteredSummaries.map((summary) => renderChatLink(summary))}
          </ul>
        </div>
      )}
    </nav>
  );
};
